import { useState } from "react";
import type { JournalEntryType } from "@roll1/shared";
import { JOURNAL_TYPE_ICONS, JOURNAL_TYPE_LABELS } from "../lib/journal-utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useI18n } from "../i18n";

interface CreateEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (type: JournalEntryType, name: string) => void;
}

const ENTRY_TYPES: JournalEntryType[] = [
  "npc",
  "location",
  "quest",
  "faction",
  "handout",
  "lore",
];

export default function CreateEntryDialog({
  open,
  onOpenChange,
  onCreate,
}: CreateEntryDialogProps) {
  const { t } = useI18n();
  const [selectedType, setSelectedType] = useState<JournalEntryType | null>(
    null,
  );
  const [name, setName] = useState("");

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedType(null);
      setName("");
    }
    onOpenChange(isOpen);
  };

  const handleCreate = () => {
    if (!selectedType || !name.trim()) return;
    onCreate(selectedType, name.trim());
    handleClose(false);
  };

  const isNameValid = name.trim().length >= 1 && name.trim().length <= 200;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="create-entry-dialog">
        <DialogHeader>
          <DialogTitle>{t("journal.create.title")}</DialogTitle>
        </DialogHeader>

        {!selectedType ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              {ENTRY_TYPES.map((type) => {
                const Icon = JOURNAL_TYPE_ICONS[type];
                return (
                  <Button
                    key={type}
                    variant="outline"
                    data-testid={`create-entry-type-${type}`}
                    className="flex items-center gap-2 justify-start h-auto py-3"
                    onClick={() => setSelectedType(type)}
                  >
                    <Icon size={18} />
                    <span>{t(`journal.type.${type}`)}</span>
                  </Button>
                );
              })}
            </div>
            <div className="flex justify-end mt-2">
              <Button variant="ghost" size="sm" onClick={() => handleClose(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-ui text-muted-foreground">
              {(() => {
                const Icon = JOURNAL_TYPE_ICONS[selectedType];
                return <Icon size={16} />;
              })()}
              <span>{t(`journal.type.${selectedType}`)}</span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => setSelectedType(null)}
              >
                {t("common.back")}
              </Button>
            </div>
            <Input
              data-testid="create-entry-name"
              placeholder={t("journal.create.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isNameValid) handleCreate();
              }}
              maxLength={200}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => handleClose(false)}>
                {t('common.cancel')}
              </Button>
              <Button data-testid="create-entry-submit" onClick={handleCreate} disabled={!isNameValid}>
                {t("journal.create.submit")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
